import { MenuItem, MenuList } from '@chakra-ui/menu';
import type { ComponentProps } from 'react';

import { showAlertDialog } from '../../../shared/components/alert-dialog';
import type { ReportRun } from '../../CustomDash/components/Reports/types';
import { useReportRunMutations } from '../../CustomDash/hooks/useReportRunMutations';
import { buildPathFromEntityType } from '../../CustomDash/utils/routing-helpers';

type ReportRunMenuListAction = 'delete';

export type CustomReportMenuListProps = ComponentProps<typeof MenuList> & {
  reportRun: ReportRun;
  viewReportLink?: string | null;
  onActionTriggered?: (action: ReportRunMenuListAction, runId: string) => void;
  onActionSucceeded?: (action: ReportRunMenuListAction, runId: string) => void;
};

export const CustomReportMenuList = ({
  reportRun,
  viewReportLink,
  onActionTriggered,
  onActionSucceeded,
  ...rest
}: CustomReportMenuListProps) => {
  const { handleDeleteReportRun } = useReportRunMutations();

  const computedViewLink = viewReportLink
    ? viewReportLink
    : buildPathFromEntityType('report', {
        templateId: reportRun.reportId,
        search: reportRun.filters ? `?filters=${reportRun.filters}` : undefined,
      });

  const onDeleteRun = () => {
    showAlertDialog({
      alertType: 'confirm',
      isOpen: true,
      headerText: 'Are you sure you want to delete this run?',
      bodyContent: <span>This action is permanent and cannot be undone.</span>,
      footerAcceptIsDestructive: true,
      footerAcceptText: 'Yes, Delete',
      onAccept: async () => {
        onActionTriggered && onActionTriggered('delete', reportRun.id);
        try {
          await handleDeleteReportRun.mutateAsync(reportRun.id);
          onActionSucceeded && onActionSucceeded('delete', reportRun.id);
        } catch {
          // noop: errors are surfaced by the mutation hook caller if needed
        }
      },
    });
  };

  return (
    <MenuList {...rest}>
      {computedViewLink && (
        <MenuItem as="a" href={computedViewLink}>
          View
        </MenuItem>
      )}

      <MenuItem onClick={onDeleteRun} color="red" _focus={{ bg: 'red.100' }}>
        Delete
      </MenuItem>
    </MenuList>
  );
};
